import {useEffect, useRef, useReducer, useMemo} from 'react';
import {flushSync} from 'react-dom';
import {Map, is} from 'immutable';
import {isEqual} from 'lodash';
import {
  buildBindingAndConditionPropsMapperCreator,
  buildCommerceOrderBindingAndConditionPropsMapper,
  buildCommerceOrderPropsMapperCreator,
  buildSearchBindingPropsMapper,
  buildUserSubscriptionBindingPropsMapper,
  buildWrapperPropsMapperCreator,
  searchResultWrapperPropsMapperCreator,
  type UpdatedProps,
} from '@packages/systems/dynamo/utils/DynamoQuery';
import {
  dynamoWrapperElementHasDynBindingOrCondition,
  dynamoWrapperElementDynDataChanged,
} from './DynamoWrapperTreeUtils';
import {
  get,
  getIn,
  getElse,
  memoFromJS,
  toJS,
} from '@packages/systems/core/utils/ImmUtils';
import {
  COMMERCE_ORDER_ROOT_COLLECTION_ID,
  COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID,
  COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID,
  COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID,
  COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID,
  COMMERCE_CART_COLLECTION_ID,
  COMMERCE_DOWNLOAD_FILES_COLLECTION_ID,
  COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {USER_SUBSCRIPTION_COLLECTION_ID} from '@packages/systems/users/gateway';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';
import {emptyArray, constant} from '@packages/utilities/fp/utils';

import type {
  Adapter,
  AdaptedGraphqlData,
  StaticBindings,
  PathToReplaceableString,
  Variables,
  DynamoPropsInput,
} from './types';
import {
  EvalEnv,
  ExpressionPath,
  Expressions,
  type PluginsContext,
} from '@packages/systems/wfdl';

import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {
  RESULTS_LENGTH_DEFAULT,
  SNIPPET_LENGTH_DEFAULT,
} from '@packages/systems/core/constants/SiteSearch';

import type {DocumentNode} from 'graphql/language';
import type {ObservableQuery, ErrorLike} from '@apollo/client';
import {reportBugOnce} from '@packages/utilities/reporter';

import {
  setEmptyDynamoEnv,
  __HACK__setDynamoEnv,
} from '@packages/systems/dynamo/graphql';
import type {RawData} from '@packages/systems/dynamo/source-types';
import {SiteType} from '@packages/systems/core/records/Site';
import {useRuntimeEnvironment, useSite} from '@packages/systems/render';
import {
  isCommerceOrderWrapperElement,
  isDynamicBoundOrConditioned,
  isDynamoWrapper,
  isSearchBound,
  isSearchResultWrapper,
} from '@packages/utilities/elementTypeUtils';
import {
  getPaginationLimit,
  getSearchBindingsFromElement,
  hasDynamoUniversalBindings,
  isPaginatedDynamoWrapperElement,
  isUnboundDynamoWrapper,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {haveDifferentUniversalBindings} from './haveDifferentUniversalBindings';

// Site getters
const getSiteShortName: (site: SiteType) => SiteType['shortName'] = getElse(
  'shortName',
  '_'
);
const getDatabaseId: (site: SiteType) => SiteType['database'] = get('database');

const exprDynPath: ExpressionPath = [
  {
    in: 'Record',
    at: 'dyn',
  },
];

// Element data getters
const getDynDataForElement = (
  el: ExpressionElement
): Map<string, Expression> | null => {
  const field = Expressions.getIn(Expressions.getElementData(el), exprDynPath);
  if (field && Expressions.isRecord(field)) {
    return memoFromJS(Expressions.getRecord(field));
  }
  return null;
};

const exprResultsLengthPath: ExpressionPath = [
  {in: 'Record', at: 'search'},
  {in: 'Record', at: 'resultsLength'},
];

const getSearchResultsLengthForElement = (el: ExpressionElement) => {
  const field = Expressions.getIn(
    Expressions.getElementData(el),
    exprResultsLengthPath
  );
  if (field && Expressions.isNumber(field)) {
    return Expressions.getNumber(field) ?? RESULTS_LENGTH_DEFAULT;
  } else {
    return RESULTS_LENGTH_DEFAULT;
  }
};

const exprSnippetLengthPath: ExpressionPath = [
  {in: 'Record', at: 'search'},
  {in: 'Record', at: 'snippetLength'},
];

const getSearchSnippetLengthForElement = (el: ExpressionElement) => {
  const field = Expressions.getIn(
    Expressions.getElementData(el),
    exprSnippetLengthPath
  );
  if (field && Expressions.isNumber(field)) {
    return Expressions.getNumber(field) ?? SNIPPET_LENGTH_DEFAULT;
  } else {
    return SNIPPET_LENGTH_DEFAULT;
  }
};

const exprSearchPreviewPath: ExpressionPath = [
  ...TEMP_PATH,
  {in: 'Record', at: 'searchPreviewQuery'},
];

const getSearchPreviewQueryForElement = (el: ExpressionElement) => {
  const field = Expressions.getIn(
    Expressions.getElementData(el),
    exprSearchPreviewPath
  );
  if (field && Expressions.isText(field)) {
    return Expressions.getText(field) || '';
  } else {
    return '';
  }
};

// PluginsContext getters
const getDynamoApolloClient = getIn(['Dynamo', 'apolloClient']);
const getCommerceApolloClient = getIn(['Commerce', 'apolloClient']);
const getDynamoCollectionId = getIn(['Dynamo', 'collectionId']);
const getPaginationPageNumbers = getElse(['Pagination', 'pageNumbers'], {});
const getDynamoCachedItems = getElse(['Dynamo', 'cachedItems'], Map());
const getDynamoItemId = getIn(['Dynamo', 'itemId']);
const getDynamoContextItemId = getIn(['Dynamo', 'contextItemId']);
const getDynamoData = getIn(['Dynamo', 'dynamoData']);
const getCommerceData = getIn(['Commerce', 'data']);
const getCommerceBindingPathPrefix = getElse(
  ['Commerce', 'bindingPathPrefix'],
  emptyArray
);
const getSearchItem = getIn(['Search', 'item']);
const getSearchPublishTarget = getIn(['Search', 'publishTarget']);
const getSearchHighlightingEnabled = getElse(
  ['Search', 'highlightingEnabled'],
  false
);
const getUsersBindingPathPrefix = getElse(
  ['Users', 'bindingPathPrefix'],
  emptyArray
);

type EnvMapper = (arg1: EvalEnv.EvalEnvType) => EvalEnv.EvalEnvType;
type PropsMapper = (
  props: DynamoPropsInput,
  env: EvalEnv.EvalEnvType
) => UpdatedProps | null;
type PropsMapperCreator = (arg1: {
  data: AdaptedGraphqlData;
  loading?: boolean;
}) => PropsMapper;

function noopPropsMapper() {
  return null;
}

const noopEnvMapper = setEmptyDynamoEnv;

function noopAdapter(data: RawData) {
  return {data, setDynamoEnv: noopEnvMapper};
}

type Ref<T> = {
  current: T;
};

type RefMutators = {
  resetRefs: () => RefMutators;
  setPropsMapperRef: (arg1: PropsMapper) => RefMutators;
  setEnvMapperRef: (arg1: EnvMapper) => RefMutators;
  setPropsMapperCreatorRef: (arg1: PropsMapperCreator | null) => RefMutators;
  setQueryRef: (arg1: DocumentNode | null, arg2: Adapter) => RefMutators;
  setVariablesRef: (arg1: Variables | null) => RefMutators;
  setObsQueryRef: (arg1: ObservableQuery) => RefMutators;
  setShouldCheckLoadingRef: (arg1: boolean) => RefMutators;
  setShouldRefetchDataRef: (arg1: boolean) => RefMutators;
};

type Refs = {
  queryRef: Ref<DocumentNode | null>;
  variablesRef: Ref<Variables | null>;
  adapterRef: Ref<Adapter>;
  propsMapperCreatorRef: Ref<PropsMapperCreator | null>;
  propsMapperRef: Ref<PropsMapper>;
  envMapperRef: Ref<EnvMapper>;
  shouldCheckLoadingRef: Ref<boolean>;
  shouldRefetchDataRef: Ref<boolean>;
  obsQueryRef: Ref<ObservableQuery | null>;
  refMutators: RefMutators;
};

function useRefs(): Refs {
  const queryRef = useRef<DocumentNode | null>(null);
  const variablesRef = useRef<Variables | null>(null);
  const adapterRef = useRef<Adapter>(noopAdapter);
  const propsMapperCreatorRef = useRef<PropsMapperCreator | null>(null);
  const propsMapperRef = useRef<PropsMapper>(noopPropsMapper);
  const envMapperRef = useRef<EnvMapper>(noopEnvMapper);
  const shouldCheckLoadingRef = useRef<boolean>(true);
  const shouldRefetchDataRef = useRef<boolean>(false);
  const obsQueryRef = useRef<ObservableQuery | null>(null);

  // Using useMemo here so that we don't unnecessarily create new mutators on
  // every render. We have everything we need already after creating the all
  // the refs above.
  return useMemo(() => {
    return {
      queryRef,
      variablesRef,
      adapterRef,
      propsMapperCreatorRef,
      propsMapperRef,
      envMapperRef,
      shouldCheckLoadingRef,
      shouldRefetchDataRef,
      obsQueryRef,
      refMutators: {
        // All the mutators return `this` so that mutator calls can be chained.
        resetRefs() {
          queryRef.current = null;
          variablesRef.current = null;
          obsQueryRef.current = null;
          shouldCheckLoadingRef.current = true;
          shouldRefetchDataRef.current = false;
          adapterRef.current = noopAdapter;
          propsMapperRef.current = noopPropsMapper;
          propsMapperCreatorRef.current = null;
          envMapperRef.current = noopEnvMapper;
          return this;
        },
        setPropsMapperRef(propsMapper: PropsMapper) {
          propsMapperRef.current = propsMapper;
          return this;
        },
        setEnvMapperRef(envMapper: EnvMapper) {
          envMapperRef.current = envMapper;
          return this;
        },
        setQueryRef(query: DocumentNode | null, adapter: Adapter) {
          queryRef.current = query;
          adapterRef.current = adapter;
          return this;
        },
        setPropsMapperCreatorRef(
          propsMapperCreator: PropsMapperCreator | null
        ) {
          propsMapperCreatorRef.current = propsMapperCreator;
          return this;
        },
        setVariablesRef(variables: Variables | null) {
          variablesRef.current = variables;
          return this;
        },
        setObsQueryRef(obsQuery: ObservableQuery) {
          shouldCheckLoadingRef.current = true;
          obsQueryRef.current = obsQuery;
          return this;
        },
        setShouldCheckLoadingRef(loading: boolean) {
          shouldCheckLoadingRef.current = loading;
          return this;
        },
        setShouldRefetchDataRef(forceRefetch: boolean) {
          shouldRefetchDataRef.current = forceRefetch;
          return this;
        },
      },
    };
  }, []);
}

function mutateQueryRefs(
  refs: Refs,
  query: DocumentNode,
  variables: Variables,
  adapter: Adapter,
  pluginsContext: PluginsContext
) {
  const {queryRef, variablesRef, obsQueryRef, refMutators} = refs;

  const queryChanged = query !== queryRef.current;
  const variablesChanged = !isEqual(variables, variablesRef.current);

  if (queryChanged) {
    refMutators.setQueryRef(query, adapter);
  }

  if (variablesChanged) {
    // Only update the reference when the `_.isEqual` check returns false
    // so that we don't change the useEffect deps unnecessarily.
    refMutators.setVariablesRef(variables);
  }

  if (!obsQueryRef.current || queryChanged || variablesChanged) {
    // Only create a new ObservableQuery when needed to limit the change
    // in the useEffect dependencies.
    const apolloClient = getDynamoApolloClient(pluginsContext);

    if (apolloClient) {
      const obsQuery = apolloClient.watchQuery({
        query: queryRef.current,
        variables: variablesRef.current,
      });
      refMutators.setObsQueryRef(obsQuery);
    } else {
      reportBugOnce('RenderDynamoError', 'Apollo Client missing', {
        team: 'platform',
        ticket: 'https://webflow.atlassian.net/browse/PLAT-1226',
      });
    }
  }
}

function mutateDynamicElementRefs(
  props: DynamoPropsInput,
  refs: Refs,
  needsQuery: boolean,
  propsMapperCreator: PropsMapperCreator,
  runtimeEnvironment: Environment
) {
  const {refMutators} = refs;

  if (!needsQuery) {
    // When we have a propsMapperCreator and no query is needed, it means that
    // we are rendering a special element, for which some parent element has
    // already fetched the GraphQL data. An example of such an element is a
    // dynamically-bound element inside Search Results List Item.
    refMutators.resetRefs().setPropsMapperRef(propsMapperCreator({data: null}));
    return;
  }

  const {pluginsContext} = props;

  const cachedItems = getDynamoCachedItems(pluginsContext);

  const cachedItem = cachedItems.get(getDynamoItemId(pluginsContext));

  if (cachedItem) {
    // If we have a cachedItem we are inside a collection list and the cachedItem
    // refers to a single entry in the list.
    refMutators
      .resetRefs()
      .setPropsMapperRef(propsMapperCreator({data: cachedItem}));

    // If contextItemId is present, then the collection list is inside
    // a parent binding context that can be used for DYN_CONTEXT conditions.
    // We supply cachedContextItem as the dynContextData argument to
    // setDynamoEnv to ensure DYN_CONTEXT bindings can be evaluated correctly.
    const contextItemId = getDynamoContextItemId(pluginsContext);
    const cachedContextItem = contextItemId
      ? cachedItems.get(contextItemId)
      : null;

    refMutators.setEnvMapperRef((env) =>
      __HACK__setDynamoEnv(env, cachedItem, cachedContextItem)
    );

    return;
  }

  if (runtimeEnvironment === WebflowEnvironments.SSR) {
    // We are in SSR and outside a collection list, which means that we need to
    // read the Dynamo data from `dynamoData` created during the top-level
    // GraphQL execution that happened before we started rendering the page.
    const {data, setDynamoEnv} = getQueryDataAndEnvMapper(props);
    refMutators
      .resetRefs()
      .setPropsMapperRef(propsMapperCreator({data}))
      .setEnvMapperRef(setDynamoEnv);
    return;
  }

  // We are neither inside a collection list nor in SSR, so we build the
  // GraphQL query, variables and adapter and store those in Refs.
  const queryInfo = getQueryInfo(props);

  if (!queryInfo || !queryInfo.query) {
    // If the query is missing there is nothing to do here
    return;
  }
  refMutators.setPropsMapperCreatorRef(propsMapperCreator);

  mutateQueryRefs(
    refs,
    queryInfo.query,
    queryInfo.variables,
    queryInfo.adapter,
    pluginsContext
  );
}

export function usePropsMapper(
  props: DynamoPropsInput,
  staticBindings: StaticBindings,
  pathToReplaceableString: PathToReplaceableString
): {
  propsMapper: PropsMapper;
  envMapper: EnvMapper;
  loading: boolean;
} {
  const {pluginsContext} = props;

  const runtimeEnvironment = useRuntimeEnvironment();

  const site = useSite();

  // `forceReRender` updates React state forcing React to re-render this component.
  // This function skips using React state for performance reasons, and instead
  // maintains its state using React Refs, and calls forceReRender when we know
  // we need to re-render with the newly fetched Dynamo data.
  const forceReRender = useForceReRender();

  // We keep track of the previous props, and return those when we are still in
  // the middle of fetching Dynamo data for the updated element tree.
  const prevPropsRef = useRef<DynamoPropsInput | undefined>(undefined);

  // `refs` contains the "state" of this hook. We are using React Refs instead
  // of React State to have absolute control over when exactly we re-render --
  // including various optimizations.
  const refs = useRefs();

  const {
    adapterRef,
    propsMapperCreatorRef,
    propsMapperRef,
    envMapperRef,
    shouldCheckLoadingRef,
    shouldRefetchDataRef,
    obsQueryRef,
    refMutators,
  } = refs;

  // This optimization is in place so that we don't unnecessarily create props
  // mappers or GraphQL queries for elements that don't need them. For example,
  // a dynamic element does not need a new propsMapper if its binding and the
  // surrounding binding context haven't changed since the previous render.
  const queryDidChange = shouldUpdateQuery(props, prevPropsRef.current);
  const shouldUpdatePropsMapper =
    queryDidChange && !neverHasQuery(props, site, runtimeEnvironment);

  // When Apollo Cache is reset, we set propsMapperRef value always back to
  // noopPropsMapper with an assumption that the next usePropsMapper invocation
  // will update propsMapperRef and refetch data from our GraphQL API.
  // shouldRefetchData is used to ensure that we update propsMapperRef and
  // refetch GraphQL data even when shouldUpdatePropsMapper is false.
  const shouldRefetchData = shouldRefetchDataRef.current;

  // Only "consume" a detected query change when we actually perform the
  // update. A plugins-context change can arrive while site.database is still
  // missing; if we advanced prevProps while opted out, the later database-only
  // update would not re-admit this element, leaving it permanently dataless.
  if (!queryDidChange || shouldUpdatePropsMapper) {
    prevPropsRef.current = props;
  }

  if (shouldUpdatePropsMapper || shouldRefetchData) {
    if (shouldRefetchData) {
      refMutators.setShouldRefetchDataRef(false);
    }

    const propsMapperCreatorInfo = getPropsMapperCreatorInfo(
      props,
      staticBindings,
      pathToReplaceableString,
      site
    );

    const newPropsMapperCreator = propsMapperCreatorInfo.propsMapperCreator;

    if (newPropsMapperCreator) {
      mutateDynamicElementRefs(
        props,
        refs,
        propsMapperCreatorInfo.needsQuery,
        newPropsMapperCreator,
        runtimeEnvironment
      );
    } else {
      // `newPropsMapperCreator` is null when an element that used to be dynamic
      // has become static. This can happen, for example, when a dynamic binding
      // was removed from an element.
      refMutators.resetRefs();
    }
  }

  // When the ObservableQuery or props mapper creator has changed, this effect
  // registers an observer for the query. Under the hood, Apollo Client fetches
  // the query if it does not have the data already cached. Once we receive the
  // new data from Apollo (either from cache or fetched from backend), we create
  // a new propsMapper using the new data, and call `forceReRender` to re-render.
  // Apollo (either from cache or fetched from backend), we create a new
  // propsMapper using the new data, and call `forceReRender` to re-render.
  useEffect(
    function runGraphqlEffect() {
      const apolloClient = getDynamoApolloClient(pluginsContext);
      const obsQuery = obsQueryRef.current;
      // Keep the creator paired with the ObservableQuery subscription created
      // by this committed effect.
      const propsMapperCreator = propsMapperCreatorRef.current;

      if (!apolloClient || !obsQuery || !propsMapperCreator) {
        return;
      }

      const adapter = adapterRef.current;

      const subscription = obsQuery.subscribe({
        next(response) {
          if (response.error) {
            if (shouldLogApolloError(response.error)) {
              console.error(response.error);
            }
            forceReRender();
            return;
          }

          if (!response.loading) {
            const {data, setDynamoEnv} = adapter(response.data || null);
            refMutators.setPropsMapperRef(propsMapperCreator({data}));
            refMutators.setEnvMapperRef(setDynamoEnv);
            forceReRender();
          }
        },
      });

      const clearListener = apolloClient.onResetStore(() => {
        // Defer the mutation of the React refs so that we never update the
        // refs while the usePropsMapper hook is reading the ref values.
        // flushSync does the trick as it flushes all the pending updates
        // outside its callback before flushing the callback synchronously.
        flushSync(() => {
          refMutators.resetRefs().setShouldRefetchDataRef(true);
        });
        // Skip automatic re-fetching of subscribed queries on Apollo client
        // cache reset because they may have become corrupt if Data Manager
        // has edited the GraphQL schema of the site's CMS.
        subscription.unsubscribe();
      });

      return function cleanupGraphqlEffect() {
        subscription.unsubscribe();
        clearListener();
      };
    },
    [obsQueryRef.current, propsMapperCreatorRef.current]
  );

  let loading = false;

  if (obsQueryRef.current && shouldCheckLoadingRef.current) {
    // We check `currentResult()` only when we know the ObservableQuery can be
    // loading as it's not the cheapest function call out there.
    const currentResult = obsQueryRef.current.getCurrentResult();
    loading = currentResult.loading;
    if (!loading) {
      // No need to keep checking loading as we know we have loaded this query.
      refMutators.setShouldCheckLoadingRef(false);
    }

    if (
      !loading &&
      !currentResult.error &&
      propsMapperCreatorRef.current &&
      adapterRef.current
    ) {
      // Query is already resolved in Apollo cache. Persist the result into
      // the refs (not just this render's return value) so that a discarded
      // (deferred) render still hands the resolved propsMapper to whichever
      // render actually commits, avoiding the Designer canvas flickering.
      const {data, setDynamoEnv} = adapterRef.current(
        currentResult.data || null
      );
      const propsMapper = propsMapperCreatorRef.current({data, loading});
      refMutators.setPropsMapperRef(propsMapper).setEnvMapperRef(setDynamoEnv);
      return {
        propsMapper,
        envMapper: setDynamoEnv,
        loading,
      };
    }
  }

  return {
    propsMapper: propsMapperRef.current,
    envMapper: envMapperRef.current,
    loading,
  };
}

function getQueryInfo(props: DynamoPropsInput): Readonly<{
  query: DocumentNode | null;
  variables: Variables;
  adapter: Adapter;
}> | null {
  const {
    originalElement: element,
    components,
    nativeIdPath,
    pluginsContext,
    env,
  } = props;

  const dynamoData = getDynamoData(pluginsContext);

  return dynamoData
    ? dynamoData.getQueryForElement(element.id, nativeIdPath, components, env)
    : null;
}

function getQueryDataAndEnvMapper(props: DynamoPropsInput): {
  data: AdaptedGraphqlData;
  setDynamoEnv: EnvMapper;
} {
  const {originalElement: element, nativeIdPath, pluginsContext} = props;
  const dynamoData = getDynamoData(pluginsContext);

  if (!dynamoData) {
    return {data: null, setDynamoEnv: noopEnvMapper};
  }

  return {
    data: dynamoData.getDataForElement(element.id, nativeIdPath),
    setDynamoEnv(env: EvalEnv.EvalEnvType) {
      return dynamoData.setDynamoEnvForElement(element.id, nativeIdPath, env);
    },
  };
}

function increment(num: number): number {
  return num + 1;
}

function useForceReRender() {
  const [_, forceUpdate] = useReducer(increment, 0);
  return forceUpdate;
}

// This function is purely meant to be a performance optimization.
// We don't want to re-generate a GraphQL query if we already know
// the query cannot have changed.
function shouldUpdateQuery(
  nextProps: DynamoPropsInput,
  prevProps?: DynamoPropsInput | null
): boolean {
  if (!prevProps) {
    return true;
  }

  const {
    pluginsContext: prevPluginsContext,
    originalElement: prevElement,
    env: prevEnv,
  } = prevProps;

  const {
    pluginsContext: nextPluginsContext,
    originalElement: nextElement,
    env: nextEnv,
  } = nextProps;

  const elementDynDataChanged =
    isDynamoWrapper(nextElement) || isCommerceOrderWrapperElement(nextElement)
      ? dynamoWrapperElementDynDataChanged(prevElement)(prevEnv)(nextElement)(
          nextEnv
        )
      : !is(
          getDynDataForElement(prevElement),
          getDynDataForElement(nextElement)
        );

  let paginationChanged = false;
  if (isDynamoWrapper(nextElement)) {
    const prevPaginationEnabled = isPaginatedDynamoWrapperElement(prevElement);
    const nextPaginationEnabled = isPaginatedDynamoWrapperElement(nextElement);
    const prevPaginationItemsPerPage = getPaginationLimit(prevElement);
    const nextPaginationItemsPerPage = getPaginationLimit(nextElement);

    if (prevPaginationEnabled !== nextPaginationEnabled) {
      paginationChanged = true;
    } else if (prevPaginationItemsPerPage !== nextPaginationItemsPerPage) {
      paginationChanged = true;
    }
  }

  const commerceContextChanged = !is(
    prevPluginsContext.get('Commerce'),
    nextPluginsContext.get('Commerce')
  );

  const paginationContextChanged =
    getPaginationPageNumbers(prevPluginsContext) !==
    getPaginationPageNumbers(nextPluginsContext);

  const searchContextChanged = !is(
    prevPluginsContext.get('Search'),
    nextPluginsContext.get('Search')
  );

  const searchBindingsChanged =
    !is(
      getSearchBindingsFromElement(prevElement),
      getSearchBindingsFromElement(nextElement)
    ) ||
    (isSearchBound(nextElement) && searchContextChanged);

  const userContextChanged = !is(
    prevPluginsContext.get('User'),
    nextPluginsContext.get('User')
  );

  const searchResultWrapperChanged = hasSearchResultWrapperChanged(
    prevElement,
    nextElement
  );

  const genericBindingsChanged = haveDifferentUniversalBindings(
    prevElement,
    nextElement
  );

  return (
    elementDynDataChanged ||
    searchBindingsChanged ||
    commerceContextChanged ||
    paginationContextChanged ||
    paginationChanged ||
    searchResultWrapperChanged ||
    userContextChanged ||
    genericBindingsChanged ||
    hasDynamoContextChanged(prevPluginsContext, nextPluginsContext)
  );
}

function hasSearchResultWrapperChanged(
  prevElement: ExpressionElement,
  nextElement: ExpressionElement
): boolean {
  if (!isSearchResultWrapper(nextElement)) {
    return false;
  }
  const prevPreviewQuery = getSearchPreviewQueryForElement(prevElement);
  const nextPreviewQuery = getSearchPreviewQueryForElement(nextElement);

  const previewQueryHasChanged =
    (typeof prevPreviewQuery === 'string' &&
      typeof nextPreviewQuery === 'string' &&
      prevPreviewQuery.trim() !== nextPreviewQuery.trim()) ||
    prevPreviewQuery !== nextPreviewQuery;

  const resultsLengthHasChanged =
    getSearchResultsLengthForElement(prevElement) !==
    getSearchResultsLengthForElement(nextElement);

  const snippetLengthHasChanged =
    getSearchSnippetLengthForElement(prevElement) !==
    getSearchSnippetLengthForElement(nextElement);

  return (
    previewQueryHasChanged || resultsLengthHasChanged || snippetLengthHasChanged
  );
}

function hasDynamoContextChanged(
  pluginsContextA: PluginsContext,
  pluginsContextB: PluginsContext
): boolean {
  const dynamoContextA = pluginsContextA.get('Dynamo');
  const dynamoContextB = pluginsContextB.get('Dynamo');

  // pruning the Dynamo context objects breaks referential equality,
  // so do a quick referential equality check before more expensive comparisons
  if (dynamoContextA === dynamoContextB) {
    return false;
  }

  // Dynamo value in pluginsContext should always be defined in practice, but
  // pluginsContext is an Immutable.Map and the value can be undefined in theory.
  // Guard against that possibility to avoid calling .delete on undefined.
  if (dynamoContextA == null || dynamoContextB == null) {
    return dynamoContextA !== dynamoContextB;
  }

  // PERFORMANCE NOTE: Prune items before comparing context objects
  // because the items object can be quite expensive to diff, and it's
  // irrelevant for determining if a new query or props mapper is necessary.
  return !is(dynamoContextA.delete('items'), dynamoContextB.delete('items'));
}

function neverHasQuery(
  props: DynamoPropsInput,
  site: SiteType,
  runtimeEnvironment: Environment
): boolean {
  const {originalElement: element, pluginsContext} = props;

  if (runtimeEnvironment === WebflowEnvironments.EXPORT) {
    return true;
  }

  if (
    !getDatabaseId(site) ||
    !getDynamoApolloClient(pluginsContext) ||
    !getCommerceApolloClient(pluginsContext)
  ) {
    // Search bindings should work without a database
    return !isSearchBound(element) && !isSearchResultWrapper(element);
  }

  return false;
}

const isDynamoWrapperWithQuery = (element: ExpressionElement) => {
  return (
    isDynamoWrapper(element) &&
    (!isUnboundDynamoWrapper(element) ||
      dynamoWrapperElementHasDynBindingOrCondition(element))
  );
};

const isElementInsideCommerceOrderBindingContext = (
  props: DynamoPropsInput
) => {
  const {pluginsContext} = props;
  switch (getDynamoCollectionId(pluginsContext)) {
    case COMMERCE_ORDER_ROOT_COLLECTION_ID:
    case COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID:
    case COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID:
    case COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID:
    case COMMERCE_CART_COLLECTION_ID:
    case COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID:
    case COMMERCE_DOWNLOAD_FILES_COLLECTION_ID:
    case COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID:
      return true;
  }
  return false;
};

const defaultPropsMapper = (_props: DynamoPropsInput): UpdatedProps | null => {
  return null;
};

const defaultPropsMapperCreator: PropsMapperCreator =
  constant(defaultPropsMapper);

type PropsMapperCreatorInfo = {
  needsQuery: boolean;
  propsMapperCreator: PropsMapperCreator | null;
};

function getPropsMapperCreatorInfo(
  props: DynamoPropsInput,
  staticBindings: StaticBindings,
  pathToReplaceableString: PathToReplaceableString,
  site: SiteType
): PropsMapperCreatorInfo {
  const {originalElement, pluginsContext} = props;

  const inCommerceOrderContext =
    isElementInsideCommerceOrderBindingContext(props);

  const dynamoCollectionId = getDynamoCollectionId(pluginsContext);

  const inUserSubscriptionContext =
    dynamoCollectionId === USER_SUBSCRIPTION_COLLECTION_ID;

  const isDynOrStaticBoundOrConditioned =
    isDynamicBoundOrConditioned(originalElement) ||
    (staticBindings && staticBindings.size > 0);

  // Pattern match the element and respond accordingly
  switch (true) {
    case isSearchBound(originalElement):
      return {
        needsQuery: false,
        propsMapperCreator: getSearchBindingPropsMapperCreator(
          props,
          pathToReplaceableString,
          site
        ),
      };

    case isSearchResultWrapper(originalElement):
      return {
        needsQuery: true,
        propsMapperCreator: searchResultWrapperPropsMapperCreator,
      };

    case isDynamoWrapperWithQuery(originalElement):
      return {
        needsQuery: true,
        propsMapperCreator: getWrapperPropsMapperCreator(props, site),
      };

    case isCommerceOrderWrapperElement(originalElement):
      return {
        needsQuery: true,
        propsMapperCreator: getCommerceOrderPropsMapperCreator(props),
      };

    case inCommerceOrderContext && isDynOrStaticBoundOrConditioned:
      return {
        needsQuery: false,
        propsMapperCreator:
          getCommerceOrderBindingAndConditionPropsMapperCreator(
            props,
            staticBindings,
            pathToReplaceableString,
            site
          ),
      };

    case inUserSubscriptionContext && isDynOrStaticBoundOrConditioned:
      return {
        needsQuery: false,
        propsMapperCreator: getUserSubscriptionBindingPropsMapperCreator(
          props,
          pathToReplaceableString,
          site
        ),
      };

    case hasDynamoUniversalBindings(originalElement):
    case isDynOrStaticBoundOrConditioned: {
      return {
        needsQuery: true,
        propsMapperCreator: getBindingAndConditionPropsMapperCreator(
          props,
          pathToReplaceableString,
          site
        ),
      };
    }
    default:
      return {needsQuery: false, propsMapperCreator: null};
  }
}

function getCommerceOrderPropsMapperCreator(
  props: DynamoPropsInput
): PropsMapperCreator {
  const {originalElement, pluginsContext, plugins} = props;
  return buildCommerceOrderPropsMapperCreator(
    originalElement,
    pluginsContext.get('Dynamo', Map()),
    plugins
  );
}

function getBindingAndConditionPropsMapperCreator(
  props: DynamoPropsInput,
  pathToReplaceableString: PathToReplaceableString,
  site: SiteType
): PropsMapperCreator {
  const {originalElement, pluginsContext, plugins} = props;
  const dynamoContext = pluginsContext.get('Dynamo');
  if (dynamoContext) {
    return buildBindingAndConditionPropsMapperCreator(
      originalElement,
      dynamoContext,
      site,
      plugins,
      pathToReplaceableString
    );
  }
  return defaultPropsMapperCreator;
}

function getUserSubscriptionBindingPropsMapperCreator(
  props: DynamoPropsInput,
  pathToReplaceableString: PathToReplaceableString,
  site: SiteType
): PropsMapperCreator {
  const {originalElement, pluginsContext, plugins} = props;

  const dynamoContext = pluginsContext.get('Dynamo', Map());
  const data = pluginsContext.getIn(['Users', 'data']);
  const usersBindingPrefix = getUsersBindingPathPrefix(pluginsContext);

  const propsMapper =
    data && Object.keys(data).length > 0
      ? buildUserSubscriptionBindingPropsMapper(
          originalElement,
          dynamoContext,
          site,
          plugins,
          pathToReplaceableString,
          data,
          usersBindingPrefix
        )
      : defaultPropsMapper;

  return () => propsMapper;
}

function getCommerceOrderBindingAndConditionPropsMapperCreator(
  props: DynamoPropsInput,
  staticBindings: StaticBindings,
  pathToReplaceableString: PathToReplaceableString,
  site: SiteType
): PropsMapperCreator {
  const {originalElement, pluginsContext} = props;
  const dynamoContext = pluginsContext.get('Dynamo', Map());
  const data = getCommerceData(pluginsContext);
  const commerceBindingPrefix = getCommerceBindingPathPrefix(pluginsContext);
  const propsMapper = buildCommerceOrderBindingAndConditionPropsMapper(
    originalElement,
    dynamoContext,
    site,
    pathToReplaceableString,
    data,
    commerceBindingPrefix,
    toJS(staticBindings)
  );
  return () => propsMapper;
}

function getSearchBindingPropsMapperCreator(
  props: DynamoPropsInput,
  pathToReplaceableString: PathToReplaceableString,
  site: SiteType
): PropsMapperCreator {
  const {originalElement, pluginsContext} = props;
  const dynamoContext = pluginsContext.get('Dynamo', Map());
  const data = getSearchItem(props.pluginsContext);
  const highlightingEnabled = getSearchHighlightingEnabled(pluginsContext);
  const publishTarget =
    getSearchPublishTarget(pluginsContext) ||
    getSiteShortName(site) + '.webflow.io';
  const propsMapper =
    data && Object.keys(data).length > 0
      ? buildSearchBindingPropsMapper(
          originalElement,
          dynamoContext,
          site,
          pathToReplaceableString,
          data,
          highlightingEnabled,
          publishTarget
        )
      : defaultPropsMapper;
  return () => propsMapper;
}

function getWrapperPropsMapperCreator(
  props: DynamoPropsInput,
  site: SiteType
): PropsMapperCreator {
  const {pluginsContext, plugins} = props;

  const apolloClient = pluginsContext.getIn(['Dynamo', 'apolloClient']);

  if (apolloClient) {
    return buildWrapperPropsMapperCreator(site, plugins);
  }

  return defaultPropsMapperCreator;
}

function shouldLogApolloError(error: ErrorLike): boolean {
  // Apollo emits "Store reset while query was in flight (not completed in link chain)"
  // when the cache is cleared or reset (e.g., during CMS changes) while a network request
  // is still in progress. The in-flight query gets canceled safely, and Apollo logs this warning.
  // It’s harmless — it doesn’t indicate a real network or GraphQL error — but it can spam logs
  // and error reporting. We explicitly ignore it here to keep our telemetry clean while still
  // surfacing genuine errors.
  return !error.message.includes('Store reset while query was in flight');
}
